import {Button, Combobox, Modal, Stack, TextInput} from "@mantine/core";
import {useForm} from "@mantine/form";
import FailureReportApi, {CreateFailureReport} from "../../../api/failure/report/FailureReportApi";
import {showNotification} from "@mantine/notifications";
import {DateTimePicker} from "@mantine/dates";
import DevicePickerDialog from "../../../components/picker-dialog/DevicePickerDialog";
import {useState} from "react";
import {Device} from "../../../api/device/DeviceApi";
import ClearButton = Combobox.ClearButton;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    closeAndUpdate: () => void;
}

const format = (dateString: string) => {
    if (dateString === null || dateString === undefined || dateString === "") {
        return undefined;
    }

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Adding 1 because getMonth() returns zero-based month
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);

    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
}

function CreateFailureReportDialog(props: Props) {
    const [relatedDevice, setRelatedDevice] = useState<Device>();
    const [relatedDeviceOpen, setRelatedDeviceOpen] = useState(false);

    const [selectedDevice, setSelectedDevice] = useState<Device>();
    const [selectedDeviceOpen, setSelectedDeviceOpen] = useState(false);

    const form = useForm<CreateFailureReport>({
        mode: 'uncontrolled',
        initialValues: {
            title: '',
            unit: '',
            dateTime: '',
            deviceNumber: '',
            storeVaccinesFrom: "",
            storeVaccinesPlace: "",
            storeVaccinesTo: "",
            fromDate: "",
            toDate: ""
        }
    });

    const onSubmit = (data: CreateFailureReport) => {
        const payload: CreateFailureReport = {
            ...data,

            storeVaccinesTo: format(data.storeVaccinesTo),
            storeVaccinesFrom: format(data.storeVaccinesFrom),
            dateTime: format(data.dateTime),
            fromDate: format(data.fromDate),
            toDate: format(data.toDate),
            relatedDeviceNumber: relatedDevice?.unit || undefined,
            unit: selectedDevice?.unit || undefined,
        }

        FailureReportApi.create(payload)
            .then(props.closeAndUpdate)
            .catch(() => showNotification({color: 'red', message: 'Nie udało się utworzyć raportu'}))
    }

    return (
        <Modal opened={props.isOpen} onClose={props.onClose} title="Nowy raport z interwencji">
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Stack gap="sm">
                    <TextInput
                        label="Tytuł raportu"
                        required
                        placeholder="Tytuł raportu z interwencji"
                        key={form.key('title')}
                        {...form.getInputProps('title')}
                    />

                    <TextInput
                        readOnly
                        withAsterisk
                        placeholder="Jednostka"
                        label="Urządzenie"
                        value={selectedDevice ? `(${selectedDevice.unit}) ${selectedDevice.name}` : '-'}
                        onClick={() => setSelectedDeviceOpen(true)}
                        rightSection={
                            <ClearButton onClear={function(): void {
                                setSelectedDevice(undefined);
                            } }/>
                        }
                    />

                    <DevicePickerDialog
                        setDevice={setSelectedDevice}
                        isOpen={selectedDeviceOpen}
                        justClose={() => setSelectedDeviceOpen(false)}
                    />

                    <DevicePickerDialog
                        setDevice={setRelatedDevice}
                        isOpen={relatedDeviceOpen}
                        justClose={() => setRelatedDeviceOpen(false)}
                    />

                    <TextInput
                        readOnly
                        label="Powiązane urządzenie"
                        value={relatedDevice?.unit ?? '-'}
                        onClick={() => setRelatedDeviceOpen(true)}
                        rightSection={
                            <ClearButton onClear={function(): void {
                                setRelatedDevice(undefined);
                            } }/>
                        }
                    />

                    <DateTimePicker
                        required
                        valueFormat="DD-MM-YYYY"
                        label="Data"
                        key={form.key('dateTime')}
                        {...form.getInputProps('dateTime')}
                    />

                    <DateTimePicker
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        label="Przechowywanie szczepionek (Od)"
                        key={form.key('storeVaccinesFrom')}
                        {...form.getInputProps('storeVaccinesFrom')}
                    />

                    <DateTimePicker
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        label="Przechowywanie szczepionek (Do)"
                        key={form.key('storeVaccinesTo')}
                        {...form.getInputProps('storeVaccinesTo')}
                    />

                    <TextInput
                        label="Miejsce przechowywania szczepionek"
                        key={form.key('storeVaccinesPlace')}
                        {...form.getInputProps('storeVaccinesPlace')}
                    />

                    <DateTimePicker
                        withAsterisk
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        label="Czas trwania (Od)"
                        description="Wymagany, jeśli z interwencji chcemy wygenerować raport."
                        key={form.key('fromDate')}
                        {...form.getInputProps('fromDate')}
                    />

                    <DateTimePicker
                        withAsterisk
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        description="Wymagany, jeśli z interwencji chcemy wygenerować raport."
                        label="Czas trwania (Do)"
                        key={form.key('toDate')}
                        {...form.getInputProps('toDate')}
                    />

                    <Button
                        fullWidth
                        type="submit"
                    >
                        Zapisz
                    </Button>
                </Stack>
            </form>
        </Modal>
    );
}

export default CreateFailureReportDialog;